import { useInfiniteQuery, useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useState, useEffect } from 'react';
import * as PipelinesService from '../services/PipelinesService';
import { Pipeline, ProvisionPipelineInput } from '../types';
import { notifyError, notifySuccess } from '../../../../components/PlatformNotification';

const DEFAULT_LIMIT = 10;

// Query keys
export const pipelinesKeys = {
    all: ['pipelines'] as const,
    lists: () => [...pipelinesKeys.all, 'list'] as const,
    list: (filters: Record<string, string>) => [...pipelinesKeys.lists(), { filters }] as const,
    details: () => [...pipelinesKeys.all, 'detail'] as const,
    detail: (id: string) => [...pipelinesKeys.details(), id] as const,
    analysis: (repositoryName: string, branchName?: string) => [...pipelinesKeys.all, 'analysis', repositoryName, branchName] as const,
    stackStatus: (stackName: string) => [...pipelinesKeys.all, 'stackStatus', stackName] as const,
};

const TRANSITIONAL_STACK_STATUSES = [
    'CREATE_IN_PROGRESS',
    'UPDATE_IN_PROGRESS',
    'UPDATE_COMPLETE_CLEANUP_IN_PROGRESS',
    'UPDATE_ROLLBACK_IN_PROGRESS',
    'UPDATE_ROLLBACK_COMPLETE_CLEANUP_IN_PROGRESS',
    'ROLLBACK_IN_PROGRESS',
    'DELETE_IN_PROGRESS',
    'REVIEW_IN_PROGRESS',
    'IMPORT_IN_PROGRESS',
    'IMPORT_ROLLBACK_IN_PROGRESS',
];

// Hooks
export const usePipelinesQuery = (filters: Record<string, string> = {}) => {
    return useInfiniteQuery({
        queryKey: pipelinesKeys.list(filters),
        queryFn: ({ pageParam }: { pageParam: string | null }) =>
            PipelinesService.fetchRecords({
                filter: filters,
                limit: DEFAULT_LIMIT,
                nextToken: pageParam,
            }),
        getNextPageParam: (lastPage) => {
            if (!lastPage || !lastPage.nextToken) return undefined;
            return lastPage.nextToken;
        },
        initialPageParam: null as string | null,
    });
};

export const usePipelineQuery = (id: string) => {
    return useQuery({
        queryKey: pipelinesKeys.detail(id),
        queryFn: () => PipelinesService.fetch(id),
        enabled: !!id,
    });
};

export const useCreatePipelineMutation = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: PipelinesService.add,
        onSuccess: () => {
            notifySuccess('Pipeline created');
            queryClient.invalidateQueries({ queryKey: pipelinesKeys.lists() });
        },
        onError: notifyError,
    });
};

export const useUpdatePipelineMutation = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: ({ id, record }: { id: string; record: Pipeline }) =>
            PipelinesService.update(id, record),
        onSuccess: (_, { id }) => {
            notifySuccess('Pipeline updated');
            queryClient.invalidateQueries({ queryKey: pipelinesKeys.lists() });
            queryClient.invalidateQueries({ queryKey: pipelinesKeys.detail(id) });
        },
        onError: notifyError,
    });
};

export const useMigrateCodePipelineMutation = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: PipelinesService.migrate,
        onSuccess: () => {
            notifySuccess('Pipeline migrated');
            queryClient.invalidateQueries({ queryKey: pipelinesKeys.all });
        },
        onError: notifyError,
    });
};

export const useCleanupMigratedPipelineMutation = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: PipelinesService.cleanup,
        onSuccess: () => {
            notifySuccess('Pipeline cleanup completed');
            queryClient.invalidateQueries({ queryKey: pipelinesKeys.all });
        },
        onError: notifyError,
    });
};

export const useDeletePipelineMutation = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: PipelinesService.deleteRecord,
        onSuccess: () => {
            notifySuccess('Pipeline deleted');
            queryClient.invalidateQueries({ queryKey: pipelinesKeys.all });
        },
        onError: notifyError,
    });
};

export const useStackStatusQuery = (stackName?: string) => {
    return useQuery({
        queryKey: pipelinesKeys.stackStatus(stackName ?? ''),
        queryFn: () => PipelinesService.fetchStackStatus(stackName as string),
        enabled: !!stackName,
        refetchInterval: (query) => {
            const status = query.state.data?.status;
            return status && TRANSITIONAL_STACK_STATUSES.includes(status) ? 15000 : false;
        },
    });
};

export const useAnalyzeRepositoryQuery = (repositoryName: string, branchName?: string) => {
    return useQuery({
        queryKey: pipelinesKeys.analysis(repositoryName, branchName),
        queryFn: () => PipelinesService.analyze({ repositoryName, branchName }),
        enabled: false,
    });
};

export const useReprovisionPipelineMutation = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: PipelinesService.reprovision,
        onSuccess: () => {
            notifySuccess('Pipeline reprovisioning started');
            queryClient.invalidateQueries({ queryKey: pipelinesKeys.all });
        },
        onError: notifyError,
    });
};

export const useProvisionPipelineMutation = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: (input: ProvisionPipelineInput) => PipelinesService.provision(input),
        onSuccess: () => {
            notifySuccess('Pipeline provisioned');
            queryClient.invalidateQueries({ queryKey: pipelinesKeys.lists() });
        },
        onError: notifyError,
    });
};

// Global filter state (shared across components)
let globalFilters: Record<string, string> = {};
let filterListeners: Set<(filters: Record<string, string>) => void> = new Set();

const notifyFilterChange = (filters: Record<string, string>) => {
    globalFilters = filters;
    filterListeners.forEach(listener => listener(filters));
};

export const usePipelinesFilter = () => {
    const [filters, setFilters] = useState<Record<string, string>>(globalFilters);

    useEffect(() => {
        const listener = (newFilters: Record<string, string>) => {
            setFilters(newFilters);
        };
        filterListeners.add(listener);
        return () => {
            filterListeners.delete(listener);
        };
    }, []);

    const handleSetFilters = (newFilters: Record<string, string>) => {
        notifyFilterChange(newFilters);
    };

    const handleClearFilters = () => {
        notifyFilterChange({});
    };

    return {
        filters,
        handleSetFilters,
        handleClearFilters,
    };
};
